import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Star } from "lucide-react";
import { WA_LINK, IMAGES } from "../site";
import { WhatsAppIcon } from "./WhatsAppIcon";

const lines = ["Unwind in", "the heart of", "Bur Dubai"];

const lineAnim = (i) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 1, delay: 0.5 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -z-10">
        <img src={IMAGES.hero} alt="Luxury massage spa interior in Bur Dubai — candle-lit steam room at Relax Massage Spa" className="w-full h-full object-cover scale-110" fetchpriority="high" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
      </motion.div>

      <motion.div style={{ y: textY }} className="max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-32 pt-40 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="text-xs md:text-sm uppercase tracking-[0.3em] text-gold-400 font-semibold mb-8 flex items-center gap-3"
          data-testid="hero-overline"
        >
          <span className="inline-block w-10 h-px bg-gold-400" />
          Relax Massage Spa — Open 24 Hours
        </motion.p>

        <h1 className="font-serif font-light tracking-tighter leading-[0.95] text-6xl sm:text-7xl md:text-8xl lg:text-9xl text-stone-100" data-testid="hero-heading">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span className="block" {...lineAnim(i)}>
                {i === 1 ? <span className="italic text-gold-300">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-12 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="max-w-md text-base md:text-lg font-light leading-relaxed text-stone-300"
            data-testid="hero-subtext"
          >
            Massage and spa rituals inside the Concorde Palace Hotel, Khalid Bin Al Waleed Road. Walk in any hour — leave reborn.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25 }}
            className="flex flex-col items-start gap-4"
          >
            <div className="flex items-center gap-2 text-sm text-stone-300" data-testid="hero-rating">
              <span className="flex text-gold-400">
                {[...Array(4)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                <Star className="w-4 h-4" />
              </span>
              <span>4.0 · 25 Google reviews</span>
            </div>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-button"
              className="group inline-flex items-center gap-3 rounded-full bg-wa px-8 py-4 text-base font-semibold text-ink transition-transform duration-300 hover:scale-[1.03] shadow-[0_0_30px_rgba(37,211,102,0.35)]"
            >
              <WhatsAppIcon className="w-5 h-5" />
              Book via WhatsApp
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-stone-400"
      >
        <ArrowDown className="w-5 h-5 animate-bounce" />
      </motion.div>
    </section>
  );
};
